"use client";

import { useState, useEffect } from "react";
import { useT } from "@/hooks/useT";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface WalletPnLEnrichedProps {
  wallet: string;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value;
  const isPositive = value >= 0;
  return (
    <div className="bg-gray-50/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] p-2 shadow-xl">
      <p className="text-[10px] text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-sm font-medium ${isPositive ? "text-[#00FF9D]" : "text-red-400"}`}>
        {isPositive ? "+" : ""}${Math.abs(value) >= 1000 ? `${(value / 1000).toFixed(1)}K` : value?.toFixed(2)}
      </p>
    </div>
  );
};

type Granularity = "day" | "week" | "month";

const GRANULARITIES: { key: Granularity; label: string }[] = [
  { key: "day", label: "D" },
  { key: "week", label: "W" },
  { key: "month", label: "M" },
];

export default function WalletPnLEnriched({ wallet }: WalletPnLEnrichedProps) {
  const t = useT();
  const [data, setData] = useState<any>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState<Granularity>("month");

  useEffect(() => {
    if (!wallet) return;
    setLoading(true);

    fetch(`/api/analytics/wallet/${wallet}/pnl?granularity=${granularity}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        // Anti-stale guard: `stale` = live Predexon (Dev) call failed, backend
        // served a possibly-old cache snapshot. Skip it → `data` stays null →
        // the card hides (see `if (!data) return null`) rather than show stale
        // realized/unrealized PnL as current.
        if (result?.stale) return;
        if (result?.success && result.data) {
          setData(result.data);
          const series = result.data.pnl_over_time || result.data.time_series || [];
          if (series.length) {
            const dateFormat: Intl.DateTimeFormatOptions = granularity === "day"
              ? { month: "short", day: "numeric" }
              : granularity === "week"
              ? { month: "short", day: "numeric" }
              : { month: "short", year: "2-digit" };
            setChartData(
              series.map((d: any) => ({
                time: new Date((d.timestamp || d.t) * 1000).toLocaleDateString("en-US", dateFormat),
                pnl: d.pnl_to_date ?? d.pnl ?? 0,
              }))
            );
          } else {
            setChartData([]);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [wallet, granularity]);

  // First fetch with nothing in memory yet → skeleton so the box stays
  // visible (the previous `return null` made the entire card vanish when
  // the user toggled D / W / M, since each toggle restarts the fetch with
  // `setLoading(true)` and the first render after that had no data).
  //
  // Once we have ANY data, render the normal UI even if a refetch is in
  // flight: the user keeps seeing the previous granularity until the new
  // one lands, instead of the card flashing to skeleton on every toggle.
  if (loading && !data) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4">
        <div className="flex items-center justify-between mb-3 animate-pulse">
          <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="flex items-center gap-2">
            <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-6 w-20 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px]" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3 animate-pulse">
          <div className="h-12 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px]" />
          <div className="h-12 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px]" />
        </div>
        <div className="h-[120px] bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const fmt = (v: number) => {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `${v >= 0 ? "+" : "-"}$${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${v >= 0 ? "+" : "-"}$${(abs / 1_000).toFixed(1)}K`;
    return `${v >= 0 ? "+" : ""}$${v.toFixed(2)}`;
  };

  const totalPnl = data.total_pnl || 0;
  const realized = data.realized_pnl || 0;
  const unrealized = data.unrealized_pnl || 0;
  const isPositive = totalPnl >= 0;

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-inter-tight text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
          {t("traders.pnlb.title")}
        </h3>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${isPositive ? "text-[#00FF9D]" : "text-red-400"}`}>
            {fmt(totalPnl)}
          </span>
          <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] p-0.5">
            {GRANULARITIES.map((g) => (
              <button
                key={g.key}
                onClick={() => setGranularity(g.key)}
                className={`text-[10px] font-semibold px-2 py-1 rounded-[5px] transition-colors ${
                  granularity === g.key
                    ? "bg-white dark:bg-white/[0.12] text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Realized / Unrealized split */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-2.5">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{t("traders.pnlb.realized")}</div>
          <div className={`text-sm font-medium ${realized >= 0 ? "text-[#00FF9D]" : "text-red-400"}`}>
            {fmt(realized)}
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-2.5">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{t("traders.pnlb.unrealized")}</div>
          <div className={`text-sm font-medium ${unrealized >= 0 ? "text-[#00FF9D]" : "text-red-400"}`}>
            {fmt(unrealized)}
          </div>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="h-[120px]">
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="pnlGradPos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00FF9D" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#00FF9D" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="pnlGradNeg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF1744" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#FF1744" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="time"
                tick={{ fontSize: 10, fill: "#6B7280" }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#333" strokeDasharray="3 3" />
              <Area
                type="monotone"
                dataKey="pnl"
                stroke={isPositive ? "#00FF9D" : "#FF1744"}
                strokeWidth={1.5}
                fill={isPositive ? "url(#pnlGradPos)" : "url(#pnlGradNeg)"}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
